import type { ReactNode } from "react";
import { cn, normalizeText } from "../../ui/utils";
import {
  AssistantTable,
  badgeMinWidth,
  numericMinWidth,
  typeMinWidth,
  type AssistantTableColumn,
} from "./AssistantTable";
import { ChartMessage } from "../charts";
import type { ChartBlock } from "../types";

function isChartBlock(obj: unknown): obj is ChartBlock {
  if (!obj || typeof obj !== "object") return false;
  const b = obj as ChartBlock;
  if (b.type !== "chart") return false;
  // Every data row must be a non-null plain object — a null/primitive row would
  // crash normalizeChart when it reads row[categoryKey] during render, which
  // (with only an app-level error boundary) would blank the entire app.
  if (Array.isArray(b.data)) {
    return b.data.every((d) => d != null && typeof d === "object" && !Array.isArray(d));
  }
  return typeof b.state === "string";
}

function parseChartBlock(src: string): ChartBlock | null {
  try {
    const obj = JSON.parse(src) as unknown;
    if (isChartBlock(obj)) return obj;
  } catch {}
  return null;
}

function renderFenced(lines: string[], lang: string, keyN: number): ReactNode {
  if (lang === "chart") {
    const chart = parseChartBlock(lines.join("\n"));
    if (chart) return <ChartMessage key={`chart-${keyN}`} spec={chart} />;
  }
  return (
    <pre
      key={`c-${keyN}`}
      className="scrollbar-neutral overflow-x-auto rounded-lg border border-border/60 bg-muted/50 p-3 text-[0.75rem] font-mono leading-relaxed text-foreground dark:border-white/10 dark:bg-white/[0.04]"
    >
      <code>{lines.join("\n")}</code>
    </pre>
  );
}

/**
 * Only allow safe link schemes. The assistant text is model-generated, so a
 * crafted `[click](javascript:...)` (or data:/vbscript:) link must never become
 * a live href. Relative paths and anchors (no scheme) are allowed; any other
 * scheme is rejected and the link renders as plain text.
 */
function safeHref(raw: string): string | null {
  const url = raw.trim();
  const scheme = url.match(/^([a-z][a-z0-9+.-]*):/i);
  if (scheme) {
    const s = scheme[1].toLowerCase();
    if (s !== "http" && s !== "https" && s !== "mailto" && s !== "tel") return null;
  }
  return url;
}

function renderInline(text: string): ReactNode[] {
  const tokenRe = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|\[[^\]\n]+\]\([^)\s]+\))/g;
  const parts = normalizeText(text).split(tokenRe);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^`[^`]+`$/.test(part)) {
      return (
        <code
          key={i}
          className="rounded-md border border-border/50 bg-muted/70 px-1.5 py-0.5 font-mono text-[0.6875rem] text-foreground dark:border-white/10 dark:bg-white/[0.06]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (/^\*[^*]+\*$/.test(part)) {
      return (
        <em key={i} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const href = safeHref(link[2]);
      if (!href) {
        return <span key={i}>{link[1]}</span>;
      }
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline-offset-2 hover:underline"
        >
          {link[1]}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function parseAlign(cell: string): "left" | "right" | "center" {
  const c = cell.trim();
  const left = c.startsWith(":");
  const right = c.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  return "left";
}

function splitTableRow(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function plainText(md: string): string {
  return md
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, "$1")
    .trim();
}

/* ── Semantic table cells ─────────────────────────────────
   The assistant's tables are plain markdown, so a few common column shapes are
   detected by header/value and rendered as richer cells: a numbered rank badge,
   a colored status pill, or a tinted type chip. Everything else stays text. */

type ColumnKind = "rank" | "status" | "type" | "text";

const RANK_HEADER = /^(#|no\.?|s[.\/]?\s?no\.?|sr\.?|sl\.?|rank|s\/n|序号)$/i;
const STATUS_HEADER = /\b(status|state|severity|result|health)\b/i;
const TYPE_HEADER = /\b(type|category|kind|classification)\b/i;

function detectColumnKinds(header: string[], rows: string[][], colCount: number): ColumnKind[] {
  return Array.from({ length: colCount }, (_, i) => {
    const h = plainText(header[i] ?? "").trim();
    if (RANK_HEADER.test(h)) return "rank";
    if (STATUS_HEADER.test(h)) return "status";
    if (TYPE_HEADER.test(h)) return "type";
    if (i === 0 && !h) {
      const vals = rows.map((r) => plainText(r[i] ?? "").trim());
      if (vals.length > 0 && vals.every((v) => /^\d{1,3}$/.test(v))) return "rank";
    }
    return "text";
  });
}

function RankBadge({ value }: { value: string }) {
  return (
    <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md bg-primary/10 px-1 text-[0.6875rem] font-semibold tabular-nums text-primary dark:bg-primary/15">
      {value}
    </span>
  );
}

const STATUS_TONES: Array<[RegExp, { dot: string; chip: string }]> = [
  [
    /^(active|enabled|passing|pass(ed)?|compliant|complete(d)?|done|approved|resolved|healthy|ready|published|live|success|ok|met)$/i,
    { dot: "bg-emerald-500", chip: "border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" },
  ],
  [
    /^(fail(ed|ing)?|non[-\s]?compliant|rejected|error|critical|blocked|overdue|expired|breach(ed)?|not met)$/i,
    { dot: "bg-red-500", chip: "border-red-500/25 bg-red-500/10 text-red-600 dark:text-red-300" },
  ],
  [
    /^(high)$/i,
    { dot: "bg-orange-500", chip: "border-orange-500/25 bg-orange-500/10 text-orange-600 dark:text-orange-300" },
  ],
  [
    /^(draft|pending|in[-\s]?progress|review|reviewing|warning|partial|medium|at[-\s]?risk|waiting|queued|scheduled)$/i,
    { dot: "bg-amber-500", chip: "border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-300" },
  ],
  [
    /^(info|new|low|note|planned)$/i,
    { dot: "bg-indigo-500", chip: "border-indigo-500/25 bg-indigo-500/10 text-indigo-600 dark:text-indigo-300" },
  ],
  [
    /^(inactive|disabled|archived|closed|deprecated|n\/?a|none|unknown|not applicable)$/i,
    { dot: "bg-slate-400", chip: "border-slate-400/25 bg-slate-400/10 text-slate-500 dark:text-slate-300" },
  ],
];

function StatusPill({ value }: { value: string }) {
  const v = value.trim();
  if (!v) return null;
  const tone = STATUS_TONES.find(([re]) => re.test(v))?.[1] ?? {
    dot: "bg-slate-400",
    chip: "border-border bg-muted/60 text-muted-foreground",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium",
        tone.chip,
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", tone.dot)} />
      {v}
    </span>
  );
}

const TYPE_TINTS = [
  "border-violet-500/25 bg-violet-500/10 text-violet-600 dark:text-violet-300",
  "border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
  "border-sky-500/25 bg-sky-500/10 text-sky-600 dark:text-sky-300",
  "border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-300",
  "border-pink-500/25 bg-pink-500/10 text-pink-600 dark:text-pink-300",
  "border-cyan-500/25 bg-cyan-500/10 text-cyan-600 dark:text-cyan-300",
];

function typeTint(value: string): string {
  let h = 0;
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) >>> 0;
  return TYPE_TINTS[h % TYPE_TINTS.length];
}

function TypePill({ value }: { value: string }) {
  const v = value.trim();
  if (!v) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-[0.6875rem] font-medium",
        typeTint(v.toLowerCase()),
      )}
    >
      {v}
    </span>
  );
}

function renderCell(raw: string, kind: ColumnKind): ReactNode {
  if (kind === "rank") {
    const v = plainText(raw).trim();
    return v ? <RankBadge value={v} /> : null;
  }
  if (kind === "status") return <StatusPill value={plainText(raw)} />;
  if (kind === "type") return <TypePill value={plainText(raw)} />;
  return renderInline(raw);
}

function MarkdownTable({
  header,
  aligns,
  rows,
}: {
  header: string[];
  aligns: ("left" | "right" | "center")[];
  rows: string[][];
}) {
  const colCount = Math.max(header.length, ...rows.map((r) => r.length), 1);
  const kinds = detectColumnKinds(header, rows, colCount);

  const maxLen = (i: number): number => {
    let m = plainText(header[i] ?? "").length;
    for (const row of rows) m = Math.max(m, plainText(row[i] ?? "").length);
    return m;
  };

  // A column that holds only numbers / percentages / currency stays compact and
  // on one line so a value like "94%" or "1,204" never breaks across two rows.
  const isNumericCol = (i: number): boolean => {
    const vals = rows.map((r) => plainText(r[i] ?? "").trim()).filter((v) => v.length > 0);
    return vals.length > 0 && vals.every((v) => /^[$£€]?\s?\d[\d.,%+\-\s/x]*$/i.test(v));
  };

  // The priority (title) column is the plain-text column with the LONGEST content
  // - the descriptive column (e.g. "Title"/"Description") that needs the most room
  // to wrap cleanly at word boundaries. Picking the first text column instead
  // would starve a long description and force mid-word breaks. It also gets the
  // heading typography in card/stacked mode.
  let titleIdx = -1;
  let titleLen = -1;
  for (let i = 0; i < colCount; i++) {
    if (kinds[i] === "text" && !isNumericCol(i)) {
      const len = maxLen(i);
      if (len > titleLen) {
        titleLen = len;
        titleIdx = i;
      }
    }
  }
  if (titleIdx < 0) titleIdx = kinds.findIndex((k) => k !== "rank");
  if (titleIdx < 0) titleIdx = 0;

  // Column model: badges / numbers / rank hold fixed compact minimums (nowrap, so
  // they never wrap, overlap, or clip); title and other text columns are flexible
  // and wrap their full value across lines - nothing is truncated. Flexible width
  // is shared by `grow`, weighted by content so longer columns earn more room and
  // words wrap whole instead of breaking. The shell stacks the table when the
  // columns can't fit the width readably.
  const columns: AssistantTableColumn[] = Array.from({ length: colCount }, (_, i) => {
    const kind = kinds[i];
    if (kind === "rank") return { align: "center", noWrap: true, minPx: 44, grow: 0 };
    if (kind === "status") {
      return { align: "left", noWrap: true, minPx: badgeMinWidth(maxLen(i)), grow: 0 };
    }
    if (kind === "type") {
      return { align: "left", noWrap: true, minPx: typeMinWidth(maxLen(i)), grow: 0 };
    }
    if (isNumericCol(i)) {
      return { align: "center", noWrap: true, minPx: numericMinWidth(maxLen(i)), grow: 0 };
    }
    const isTitle = i === titleIdx;
    // Priority column grows most; other text columns earn a larger share only if
    // they hold multi-word content (so short-code columns stay narrow).
    const grow = isTitle ? 3 : maxLen(i) >= 18 ? 2 : 1;
    return { align: aligns[i] ?? "left", grow, isTitle };
  });
  const headerCells = Array.from({ length: colCount }, (_, i) => renderInline(header[i] ?? ""));
  const bodyRows = rows.map((row) =>
    Array.from({ length: colCount }, (_, c) => renderCell(row[c] ?? "", kinds[c])),
  );

  return <AssistantTable columns={columns} header={headerCells} rows={bodyRows} />;
}

export function ChatMarkdown({ content }: { content: string }) {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const out: ReactNode[] = [];
  let i = 0;
  let para: string[] = [];
  let list: { kind: "ul" | "ol"; items: string[] } | null = null;
  let code: string[] | null = null;
  let codeLang = "";

  const flushPara = () => {
    if (!para.length) return;
    out.push(
      <p
        key={`p-${out.length}`}
        className="text-xs leading-relaxed text-foreground"
      >
        {renderInline(para.join(" "))}
      </p>,
    );
    para = [];
  };

  const flushList = () => {
    if (!list) return;
    const baseCls = "space-y-1.5 pl-5 text-xs leading-relaxed text-foreground marker:text-muted-foreground";
    if (list.kind === "ul") {
      out.push(
        <ul key={`l-${out.length}`} className={`${baseCls} list-disc marker:text-primary/60`}>
          {list.items.map((it, idx) => (
            <li key={idx} className="pl-0.5">
              {renderInline(it)}
            </li>
          ))}
        </ul>,
      );
    } else {
      out.push(
        <ol key={`l-${out.length}`} className={`${baseCls} list-decimal marker:font-semibold`}>
          {list.items.map((it, idx) => (
            <li key={idx} className="pl-0.5">
              {renderInline(it)}
            </li>
          ))}
        </ol>,
      );
    }
    list = null;
  };

  while (i < lines.length) {
    const line = lines[i];

    if (code !== null) {
      if (/^```/.test(line.trim())) {
        out.push(renderFenced(code, codeLang, out.length));
        code = null;
        codeLang = "";
      } else {
        code.push(line);
      }
      i++;
      continue;
    }
    if (/^```/.test(line.trim())) {
      flushPara();
      flushList();
      code = [];
      codeLang = line.trim().replace(/^`+/, "").trim().toLowerCase();
      i++;
      continue;
    }

    if (
      /^\s*\|.*\|\s*$/.test(line) &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      flushPara();
      flushList();
      const header = splitTableRow(line);
      const aligns = splitTableRow(lines[i + 1]).map(parseAlign);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      out.push(
        <MarkdownTable
          key={`t-${out.length}`}
          header={header}
          aligns={aligns}
          rows={rows}
        />,
      );
      continue;
    }

    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      flushPara();
      flushList();
      out.push(
        <hr
          key={`hr-${out.length}`}
          className="border-0 border-t border-border/40"
        />,
      );
      i++;
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushPara();
      flushList();
      const level = heading[1].length;
      const text = heading[2];
      if (level === 1) {
        out.push(
          <h3
            key={`h-${out.length}`}
            className="pt-0.5 text-[0.9375rem] font-bold leading-snug tracking-tight text-foreground"
          >
            {renderInline(text)}
          </h3>,
        );
      } else if (level === 2) {
        out.push(
          <h4
            key={`h-${out.length}`}
            className="text-[0.8125rem] font-semibold leading-snug text-foreground"
          >
            {renderInline(text)}
          </h4>,
        );
      } else {
        out.push(
          <h5
            key={`h-${out.length}`}
            className="text-[0.6875rem] font-semibold tracking-wide text-muted-foreground"
          >
            {renderInline(text)}
          </h5>,
        );
      }
      i++;
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      flushPara();
      flushList();
      const quote: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      out.push(
        <blockquote
          key={`q-${out.length}`}
          className="rounded-r-lg border-l-2 border-primary/50 bg-primary/[0.04] px-3 py-2 text-xs leading-relaxed text-foreground/90 dark:bg-white/[0.03]"
        >
          {renderInline(quote.join(" ").trim())}
        </blockquote>,
      );
      continue;
    }

    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ul) {
      flushPara();
      if (!list || list.kind !== "ul") {
        flushList();
        list = { kind: "ul", items: [] };
      }
      list.items.push(ul[1]);
      i++;
      continue;
    }
    if (ol) {
      flushPara();
      if (!list || list.kind !== "ol") {
        flushList();
        list = { kind: "ol", items: [] };
      }
      list.items.push(ol[1]);
      i++;
      continue;
    }

    if (!line.trim()) {
      flushPara();
      flushList();
      i++;
      continue;
    }

    flushList();
    para.push(line);
    i++;
  }

  if (code !== null) {
    out.push(renderFenced(code, codeLang, out.length));
  }
  flushPara();
  flushList();

  return <div className="space-y-3">{out}</div>;
}
